'use client';

import { useState, useEffect, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useAuthStore } from '@/store/auth';
import { useChatHistoryStore } from '@/store/chat';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { AuthPageLayout, AuthCard } from '@/components/auth';
import { GOOGLE_CONFIG } from '@/lib/config';
import GoogleLoginButton from '@/components/GoogleLoginButton';

type LoginStep = 'email' | 'password' | 'signup';

function LoginContent() {
  const t = useTranslations('auth.login');
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get('redirect'); // e.g. /mcp-auth?client_id=...
  const { login, isAuthenticated, error, clearError, setError, setLoading, isLoading, setUser } = useAuthStore();

  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [mounted, setMounted] = useState(false);

  // Handle client-side only mounting
  useEffect(() => {
    setMounted(true);
    clearError();
    // Debug: Check if Google Client ID is available
    console.log('Google Client ID available:', !!GOOGLE_CONFIG.clientId);
    console.log('Google Config:', GOOGLE_CONFIG);

    // Surface error codes returned by the OAuth redirect flow
    const errorCode = searchParams.get('error');
    if (errorCode) {
      const errorMessages: Record<string, string> = {
        pending_approval: t('errorPendingApproval'),
        account_locked: t('errorAccountLocked'),
        email_conflict: t('errorEmailConflict'),
        invalid_state: t('errorInvalidState'),
        token_exchange_failed: t('errorTokenExchange'),
        invalid_id_token: t('errorAuthFailed'),
        auth_failed: t('errorAuthFailed'),
        exchange_failed: t('errorExchangeFailed'),
      };
      setError(errorMessages[errorCode] || t('errorGoogleFailed'));

      // Strip the ?error= param so a page refresh does not re-show the error.
      const urlObj = new URL(window.location.href);
      urlObj.searchParams.delete('error');
      window.history.replaceState({}, '', urlObj.toString());
    }
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (!mounted) return;  // Wait for Zustand rehydration
    if (isAuthenticated) {
      const dest = redirectTo && redirectTo.startsWith('/') ? redirectTo : '/chat';
      router.push(dest);
    }
  }, [mounted, isAuthenticated, router, redirectTo]);

  // Clear errors when email changes
  useEffect(() => {
    clearError();
    setEmailError('');
    setPasswordError('');
  }, [email, clearError]);

  const validateEmail = (email: string): boolean => {
    if (!email.trim()) {
      setEmailError(t('emailRequired'));
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError(t('emailInvalid'));
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail(email)) {
      return;
    }

    setLoading(true);
    clearError();

    try {
      setStep('password');
    } catch (error: any) {
      console.error('Error checking email:', error);
      setStep('signup');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password.trim()) {
      setPasswordError(t('passwordRequired'));
      return;
    }

    if (password.length < 6) {
      setPasswordError(t('passwordTooShort'));
      return;
    }

    setLoading(true);
    clearError();

    try {
      console.log('Attempting login with:', email);
      // Backend expects username, so we'll send email as username
      const response = await api.auth.login({
        username: email,
        password: password
      });
      console.log('Login response:', response.data);

      const { access_token, token_type } = response.data;
      const refresh_token = response.data.refresh_token || '';

      // Store tokens first (create a temporary user object)
      const tempUser = {
        id: 0,
        username: email,
        email: email,
        name: '',
        role: 'user',
        created_at: ''
      };

      login(
        { access_token, refresh_token, token_type },
        tempUser
      );

      // Get user info with the new token
      try {
        const userResponse = await api.auth.me();
        console.log('User info:', userResponse.data);

        // Update user info
        setUser(userResponse.data);

        // Clear active conversation to start fresh
        useChatHistoryStore.getState().setActiveConversation(null);

        // Small delay to ensure store persists to localStorage before navigation
        await new Promise(resolve => setTimeout(resolve, 50));

        // Redirect to chat page or MCP auth destination
        const dest = redirectTo && redirectTo.startsWith('/') ? redirectTo : '/chat';
        router.push(dest);
      } catch (userError: any) {
        console.error('Failed to get user info:', userError);
        // Still redirect even if user info fails
        const dest = redirectTo && redirectTo.startsWith('/') ? redirectTo : '/chat';
        router.push(dest);
      }
    } catch (error: any) {
      console.error('Login error:', error);

      // Handle specific error cases
      if (error.response?.status === 401) {
        setPasswordError(t('passwordIncorrect'));
      } else if (error.response?.status === 403) {
        const detail = error.response?.data?.detail || '';
        if (detail.includes('pending approval') || detail.includes('pending admin approval')) {
          setError(t('errorPendingApproval'));
        } else if (detail.includes('locked')) {
          setError(detail);
        } else {
          setError(t('errorAccessDenied'));
        }
      } else if (error.response?.status === 404) {
        // User doesn't exist, redirect to signup
        setStep('signup');
      }
      // Other errors are handled by the API interceptor
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('email');
    setPassword('');
    setPasswordError('');
    clearError();
  };

  // Prevent hydration mismatch by not rendering until client-side
  if (!mounted) {
    return null;
  }

  return (
    <AuthPageLayout>
      <AuthCard
        title={t('title')}
        description={t('description')}
        footerText={step === 'signup' ? '' : t('noAccount')}
        footerLinkText={step === 'signup' ? '' : t('signUp')}
        footerLinkHref="register"
      >
        <div className="space-y-s6">
          {/* Google Sign-In - only show on email step and when configured */}
          {step === 'email' && GOOGLE_CONFIG.clientId && (
            <>
              <GoogleLoginButton onError={setError} redirectTo={redirectTo || undefined} />

              {/* Divider with OR */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-s4 bg-[#2a1a3e] text-[rgb(var(--text-muted))] uppercase tracking-wider">{t('or')}</span>
                </div>
              </div>
            </>
          )}

          {/* Email Step */}
          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-s4">
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder={t('emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  autoFocus
                  className="w-full px-s4 py-s3 rounded-lg bg-transparent border border-white/20 text-[rgb(var(--text-primary))] placeholder-gray-500 focus-visible:outline-none/40 focus-visible:ring-1 focus-visible:ring-accent-primary/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                />
                {emailError && (
                  <p className="mt-s1 text-xs text-error">{emailError}</p>
                )}
              </div>

              {error && (
                <div className="p-s3 text-sm text-error bg-error/10 border border-error/30 rounded-lg">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-white hover:bg-white/90 text-gray-900 font-medium py-s3"
                loading={isLoading}
                disabled={isLoading}
              >
                {t('continue')}
              </Button>
            </form>
          )}

          {/* Password Step */}
          {step === 'password' && (
            <div className="space-y-s4">
              {/* Show email with back button */}
              <div className="flex items-center gap-s3 mb-s2">
                <button
                  onClick={handleBack}
                  className="text-[rgb(var(--text-muted))] hover:text-[rgb(var(--text-primary))] transition-colors"
                  type="button"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div className="flex-1">
                  <p className="text-sm text-[rgb(var(--text-muted))]">{t('signingInAs')}</p>
                  <p className="text-[rgb(var(--text-primary))] font-medium">{email}</p>
                </div>
              </div>

              <form onSubmit={handlePasswordSubmit} className="space-y-s4">
                <div>
                  <input
                    type="password"
                    name="password"
                    placeholder={t('passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    autoFocus
                    className="w-full px-s4 py-s3 rounded-lg bg-transparent border border-white/20 text-[rgb(var(--text-primary))] placeholder-gray-500 focus-visible:outline-none/40 focus-visible:ring-1 focus-visible:ring-accent-primary/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  {passwordError && (
                    <p className="mt-s1 text-xs text-error">{passwordError}</p>
                  )}
                </div>

                {error && (
                  <div className="p-s3 text-sm text-error bg-error/10 border border-error/30 rounded-lg">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-white hover:bg-white/90 text-gray-900 font-medium py-s3"
                  loading={isLoading}
                  disabled={isLoading}
                >
                  {t('continue')}
                </Button>
              </form>
            </div>
          )}

          {/* Signup Step */}
          {step === 'signup' && (
            <div className="space-y-s4">
              <div className="flex items-center gap-s3 mb-s2">
                <button
                  onClick={handleBack}
                  className="text-[rgb(var(--text-muted))] hover:text-[rgb(var(--text-primary))] transition-colors"
                  type="button"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div className="flex-1">
                  <p className="text-[rgb(var(--text-primary))] font-medium">{email}</p>
                </div>
              </div>

              <div className="p-s4 bg-brand/10 border border-brand/30 rounded-lg text-center">
                <p className="text-sm text-[rgb(var(--text-secondary))] mb-s3">
                  {t('noAccountFound')}
                </p>
                <Button
                  onClick={() => router.push(`/register?email=${encodeURIComponent(email)}`)}
                  className="w-full bg-white hover:bg-white/90 text-gray-900 font-medium py-s3"
                >
                  {t('createAccount')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </AuthCard>
    </AuthPageLayout>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginContent />
    </Suspense>
  );
}
